import { useAuth } from "@/auth/AuthProvider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

const CLI_AUTH_RETURN_TO_KEY = "cli_auth_return_to";

type ValidationResult = { ok: true; redirectUri: string; state: string } | { ok: false; error: string };

function validateParams(search: string): ValidationResult {
  const params = new URLSearchParams(search);
  const redirectUri = params.get("redirect_uri");
  const state = params.get("state");

  if (!redirectUri) return { ok: false, error: "Missing redirect_uri parameter." };
  if (!state) return { ok: false, error: "Missing state parameter." };

  let parsed: URL;
  try {
    parsed = new URL(redirectUri);
  } catch {
    return { ok: false, error: "redirect_uri is not a valid URL." };
  }

  if (parsed.protocol !== "http:") {
    return { ok: false, error: "redirect_uri must use http:// (loopback only)." };
  }
  if (parsed.hostname !== "127.0.0.1" && parsed.hostname !== "localhost") {
    return { ok: false, error: "redirect_uri must be a loopback address (127.0.0.1 or localhost)." };
  }
  if (parsed.pathname !== "/callback") {
    return { ok: false, error: "redirect_uri must end in /callback." };
  }
  const port = Number(parsed.port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    return { ok: false, error: "redirect_uri must include a valid port." };
  }

  return { ok: true, redirectUri, state };
}

export default function CliAuthorize() {
  const location = useLocation();
  const { session } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const validation = useMemo(() => validateParams(location.search), [location.search]);

  // `cli_auth_return_to` is set in main.tsx so the post-sign-in redirect can
  // bring the user back here with their query string intact. Once we've
  // landed on a terminal state (invalid params — there's no useful recovery)
  // it should be cleared so a later session-expired re-auth doesn't bounce
  // the user into a stale `/cli/authorize?...`. The Cancel handler clears it
  // for the same reason; the success path clears it inside `handleApprove`.
  useEffect(() => {
    if (!validation.ok) {
      sessionStorage.removeItem(CLI_AUTH_RETURN_TO_KEY);
    }
  }, [validation.ok]);

  if (!validation.ok) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-[440px]">
          <CardHeader>
            <CardTitle>Invalid authorization request</CardTitle>
            <CardDescription>
              This page was opened with invalid parameters. Close this tab and try running the CLI command again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{validation.error}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-[440px]">
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Loading session...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-[440px]">
          <CardHeader>
            <CardTitle>All set</CardTitle>
            <CardDescription>
              The CLI has received your credentials. You can close this tab and return to your terminal.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleApprove = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at ?? null,
        email: session.user.email ?? null,
        user_id: session.user.id,
        state: validation.state,
      };
      const response = await fetch(validation.redirectUri, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Local CLI responded with HTTP ${response.status}.`);
      }
      sessionStorage.removeItem(CLI_AUTH_RETURN_TO_KEY);
      setDone(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? `Could not reach the CLI at ${validation.redirectUri}: ${error.message}`
          : "Could not reach the CLI on this device.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-[440px]">
        <CardHeader>
          <CardTitle>Authorize CLI access</CardTitle>
          <CardDescription>
            A tool running on this computer is requesting your SmartMed credentials so it can call the classifier API on
            your behalf.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1 text-sm">
            <p>
              <span className="text-muted-foreground">Signed in as: </span>
              <span className="font-medium">{session.user.email}</span>
            </p>
            <p>
              <span className="text-muted-foreground">Sending credentials to: </span>
              <span className="font-mono">{validation.redirectUri}</span>
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            Only approve if you started a CLI command on this computer and are expecting this page.
          </p>
          {submitError && (
            <Alert variant="destructive">
              <AlertTitle>Could not send credentials</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button onClick={handleApprove} disabled={submitting} className="flex-1">
              {submitting ? "Approving..." : "Approve"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                sessionStorage.removeItem(CLI_AUTH_RETURN_TO_KEY);
                window.close();
              }}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
